import React from 'react';
import { connect } from 'react-redux';
import { first } from 'lodash';
import { bindActionCreators } from 'redux';

import { beChargeCardOnFileV1 } from 'actions/payment/beChargeCardOnFile.action';
import beCreateTransaction from 'actions/payment/beCreateTransaction.action';
import feChangePaymentType from 'actions/payment/feChangePaymentType.action';
import beInitReaderPayment from 'actions/pos/beInitReaderPayment.action';
import feSetPosIntentId from 'actions/pos/feSetPosIntentId.action';
import updateProcedure from 'actions/scheduler/updateProcedure';
import ICO_FAILED from 'assets/images/dialogs/PaymentFailed.svg';
import IMG_CASH from 'assets/images/icons/cash-purple.svg';
import IMG_DEPOSIT from 'assets/images/icons/currency-pound-purple.svg';
import IMG_VOUCHER from 'assets/images/icons/gift-purple.svg';
import IMG_MONEY from 'assets/images/icons/money-purple.svg';
import IMG_PAY from 'assets/images/icons/pay-purple.png';
import IMG_STORE from 'assets/images/icons/store-purple.png';
import { checkCapFlag } from 'bl/captures.bl';
import { LT_POS } from 'bl/loggers.bl';
import determinePaymentType from 'bl/payment/determinePaymentType.bl';
import validateCheckoutData from 'bl/payment/validateCheckoutData.bl';
import determineAndStorePaymentIntentId from 'bl/pos/determineAndStorePaymentIntentId.bl';
import determinePriceLines from 'bl/pos/determinePriceLines.bl';
import DialogMessage from 'components/dialog/DialogMessage.component';
import SalonModal from 'components/dialog/SalonModal.component';
import Button from 'components/form/Button.component';
import Icon from 'components/generic/Icon.component';
import Space from 'components/generic/Space.component';
import PosReaderDialog from 'components/pos/PosReaderDialog.component';
import PRDS from 'components/pos/PosReaderDialogStatus.const';
import PosReaderPriceLine from 'components/pos/PosReaderPriceLine.component';
import PosStateMessage from 'components/pos/PosStateMessage.component';
import CurrencySign from 'components/price/CurrencySign.component';
import SENTRY from 'config/sentry.singleton';
import { USE_OLD_CARD_PAYMENT_ERROR } from 'config/use.conf';
import LINK from 'constants/link.const';
import EMSG from 'constants/messages/errorMessage.const';
import PAYMENT_ORIGIN_POS from 'constants/payment/paymentOriginPos.const';
import SF from 'constants/salonFlag.const';
import S from 'constants/string.const';
import {
  selectCheckoutClientId,
  selectCheckoutData,
  selectCheckoutPaymentOption,
  selectCheckoutPos,
  selectCheckoutSelectedAppointment,
} from 'selectors/checkout.selectors';
import {
  selectCurrentSalonFlags,
  selectCurrentSalonId,
  selectCurrentSalonPosFlow,
} from 'selectors/currentSalon.selectors';
import { selectServiceMap } from 'selectors/salonInfo.selectors';
import getAnyOrVoid from 'utils/accessors/getAnyOrVoid.util';
import getArray from 'utils/accessors/getArray.util';
import asArray from 'utils/arrays/asArray.util';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import fromAnyToInteger from 'utils/converters/fromAnyToInteger';
import { fromPropsToIsWeek, fromPropsToMatchedParams } from 'utils/converters/props/path.util';
import linkTo from 'utils/links/linkTo.util';
import navFor from 'utils/links/navFor.util';
import logger from 'utils/logger.util';
import asObject from 'utils/objects/asObject.util';
import isFullArray from 'utils/predicates/array/isFullArray.util';
import isEnumVal from 'utils/predicates/enum/isEnumVal.util';
import isNotEnumKey from 'utils/predicates/enum/isNotEnumKey.util';
import isPositiveNumber from 'utils/predicates/number/isPositiveNumber.util';

import { changeAppointmentsStatus, updateAppointmentWithProcedures } from 'booking/actions';
import { getAllVoucher, getCashupVoucher } from 'cashup/actions';
import { cancelChargeSlickCardOnline, cleanTransactionData, useDeposit } from 'checkout/actions';
import AlertDialog from 'checkout/components/AlertDialog';
import ConfirmationDialogFromCheckout from 'checkout/components/ConfirmationDialog';
import PayPopUpPriceLine from 'checkout/components/PayPopUpPriceLine.component';

import CN from './CheckoutPaymentPopUpV1.module.scss';

const CTAG = 'CheckoutPaymentPopUpV1';
const L = logger(CTAG);
const LOG_POS = logger(LT_POS);

const CAP_PPU_CONFIRM_1 = 'aborting charge due to positive disable flag';
const CAP_PPU_CONFIRM_2 = 'creating transaction due to negative check on onlineAmount > 0';
const CAP_PPU_CONFIRM_3 = 'charge card on file succeeded';
const CAP_PPU_CONFIRM_4 = 'charge card on file failed';
const CAP_PPU_CREATE_1 = 'aborting create transaction due to negative flag or already fetching';
const CAP_PPU_CREATE_2 = 'updating appointment procedures succeeded';
const CAP_PPU_CREATE_3 = 'aborting create transaction due to negative data validation result';
const CAP_PPU_CREATE_4 = 'create transaction succeeded';
const CAP_PPU_COMPLETE_1 = 'change appointment status succeeded';
const CAP_PPU_CANCEL_1 = 'confirm payment canceled due to user action';
const CAP_PPU_CONFLICT_2 = 'both success and cancel are truthy in the V2 of done';

const checkIfPosIsOpen = L.wrap('checkIfPosIsOpen()', ({ posFlow, cardAmount, posStep }) =>
  fromAnyToBool(
    //
    // NOTE: make sure POS is used only if posFLow is exactly true, not truthy
    posFlow === true && //
      // should not open the dialog if the cardAmount is 0 or invalid
      isPositiveNumber(cardAmount) && //
      // check if the posStep is actually set and it isn't one of the closed states
      isEnumVal(PRDS, posStep) &&
      posStep !== PRDS.closed &&
      posStep !== PRDS.cancel,
  ),
);

const checkIfPosIsFinished = L.wrap('checkIfPosIsFinished()', ({ posFlow, posStep }) => {
  const isPosTransaction = posFlow === true; // NOTE: make sure POS is used only if posFLow is exactly true, no truthy
  return !isPosTransaction || posStep === PRDS.success;
});

const sumDeposits = (depositDataList) => {
  let total = 0;
  for (const { use_deposit: d } of depositDataList) {
    total += d;
  }
  return total;
};

const sumVouchers = (usedVouchers) => {
  if (!usedVouchers) {
    return 0;
  }

  let total = 0;
  for (const voucher of usedVouchers) {
    if (voucher) {
      total += voucher.used_amount;
    }
  }

  return total;
};

const extractPaymentInfo = L.wrap('extractPaymentInfo()', (props) => {
  const o = asObject(props);
  const d = asObject(o.data);

  return {
    salonId: o.salonId,
    appointmentId: first(o.appointments),
    clientId: getAnyOrVoid('0.id', d.clients),
    chargeCard: d.onlineAmount > 0,
    voucherAmount: sumVouchers(d.vouchers_used),
    depositAmount: sumDeposits(d.depositListData),
    cardNumberEnd: fromAnyToTrimmed(d.active_card),
    cardAmount: d.cardAmount,
    cashAmount: d.partCash,
    otherAmount: d.otherAmount,
    onlineAmount: d.onlineAmount,
    total: d.total,
    items: [{ description: '', price: 0, quantity: 0 }],
  };
});

// DEPRECATED: remove this component and use src/checkout/components/CheckoutPaymentPopUpV2.component.jsx instead
class CheckoutPaymentPopUpV1 extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      disableDuplicateTransaction: false,
      disableChargeClient: false,
      confirmUpdateDialog: false,
      totalValidate: true,
    };
  }

  captureInfo = (logger, tags, extra, message) => {
    const flags = this.props.salonFlags;
    const prefix = SF.PayPopUp;
    if (checkCapFlag({ prefix, flags }).info) SENTRY.info({ logger, tags, extra, message });
  };

  captureWarn = (logger, tags, extra, message) => {
    const flags = this.props.salonFlags;
    const prefix = SF.PayPopUp;
    if (checkCapFlag({ prefix, flags }).warn) SENTRY.warn({ logger, tags, extra, message });
  };

  captureError = (logger, tags, extra, error) => {
    const flags = this.props.salonFlags;
    const prefix = SF.PayPopUp;
    if (checkCapFlag({ prefix, flags }).error) SENTRY.error({ logger, tags, extra, error });
  };

  startConfirming = (options) => {
    const tag = options?.tag;
    const { isConfirming } = this.state;

    LOG_POS.debug('CheckoutPaymentPopUpV1.startConfirming()', { tag }, isConfirming, '->', true);
    L.debug('startConfirming()', { tag }, isConfirming, '->', true);
    this.setState({ isConfirming: true });
  };

  stopConfirming = (options) => {
    const tag = options?.tag;
    const { isConfirming } = this.state;

    LOG_POS.debug('CheckoutPaymentPopUpV1.stopConfirming()', { tag }, isConfirming, '->', false);
    L.debug('stopConfirming()', { tag }, isConfirming, '->', false);
    this.setState({ isConfirming: false });
  };

  confirm = async () => {
    const FTAG = 'confirm()';
    const capLogger = [CTAG, FTAG];

    const { salonId, beChargeCardOnFile, appointments: appointmentIds, data } = this.props;
    const { disableChargeClient } = this.state;
    const { onlineAmount, clients } = asObject(data);

    const capTags = { salonId };
    const capExtra = { ...capTags, onlineAmount, appointmentIds, disableChargeClient };

    if (disableChargeClient) {
      this.captureInfo(capLogger, capTags, capExtra, CAP_PPU_CONFIRM_1);
      return;
    }

    this.setState({ disableChargeClient: true });

    if (!isPositiveNumber(onlineAmount)) {
      this.captureInfo(capLogger, capTags, capExtra, CAP_PPU_CONFIRM_2);
      try {
        await this.performTransaction();
      } catch (e) {
        L.warn(FTAG, 'performTransaction error:', e);
        this.captureError(capLogger, capTags, capExtra, e);
      }
      return;
    }

    this.setState({ errorMessage: S.empty });

    try {
      const appointmentId = first(appointmentIds);
      const firstClientId = first(clients).id;
      const clientId = firstClientId ? fromAnyToInteger(firstClientId) : 0;
      const options = { salonId, clientId, appointmentId, amount: onlineAmount };

      const chargeResult = await beChargeCardOnFile(options);

      const chargeSuccess = getAnyOrVoid('response.success', chargeResult);
      const paymentIntentId = getAnyOrVoid('response.intent_id', chargeResult);
      const httpStatusCode = getAnyOrVoid('actualResponse.status', chargeResult);

      const capTags2 = { salonId, clientId, appointmentId, action: 'beChargeCardOnFile' };
      const capExtra2 = { ...capTags2, ...capExtra, firstClientId, httpStatusCode, chargeResult };

      if (!chargeResult.is.ok || !chargeSuccess) {
        L.warn(FTAG, 'chargeResult:', chargeResult, 'not setting paymentIntentId:', paymentIntentId);
        this.captureWarn(capLogger, capTags2, capExtra2, CAP_PPU_CONFIRM_4);
        this.setState({ errorMessage: EMSG.txCreate });
        return;
      }

      L.debug(FTAG, 'chargeResult:', chargeResult, 'setting paymentIntentId:', paymentIntentId);
      this.setState({ paymentIntentId });
      await this.performTransaction();
      this.captureInfo(capLogger, capTags2, capExtra2, CAP_PPU_CONFIRM_3);
    } catch (e) {
      L.warn(FTAG, 'beChargeCardOnFile error:', e);
      this.captureError(capLogger, capTags, capExtra, e);
    }
  };

  performTransaction = async () =>
    // NOTE: on abort, initTransaction will return false, otherwise it may simply throw error before complete is called
    (await this.initTransaction()) && this.completeTransaction();

  initTransaction = async () => {
    const FTAG = 'initTransaction()';
    const capLogger = [CTAG, FTAG];

    const { disableDuplicateTransaction, paymentIntentId } = this.state;
    L.debug(FTAG, 'Current state paymentIntentId:', paymentIntentId);

    const {
      updateAppointmentWithProcedures,
      data: dataFromProps,
      feChangePaymentType,
      beCreateTransaction,
      transaction,
      procedures: proceduresFromProps,
      appointments: appointmentIds,
      appointmentDetails,
      salonId,
      posFlow,
      checkoutOption,
    } = this.props;

    const { isFetching } = asObject(transaction);
    const { onlineAmount, cardAmount, depositListData: depositListDataFromProps } = dataFromProps;

    const isPosEnabled = posFlow === true; // NOTE: make sure POS is used only if posFLow is exactly true
    const paymentType = determinePaymentType({ checkoutOption, cardAmount, onlineAmount, isPosEnabled });

    const capTags1 = { salonId, paymentType, paymentIntentId };
    const capExtra1 = { ...capTags1, isFetching, disableDuplicateTransaction, appointmentIds };

    if (isFetching || disableDuplicateTransaction) {
      this.captureInfo(capLogger, capTags1, capExtra1, CAP_PPU_CREATE_1);
      return false;
    }

    this.setState({ disableDuplicateTransaction: true });

    const procedures = asArray(proceduresFromProps);
    const servicesOfFirstClient = getArray('clients[0].services', dataFromProps);

    // NOTE: depositListData might be set each cycle near the end of the big loop and is a shared mutable state
    let depositListData;

    feChangePaymentType({ paymentType });

    // TODO: determine why this needs updated after both for cycles finish
    let filteredProcedures = void 1;

    for (const appointmentId of asArray(appointmentIds)) {
      const updatedProcedures = [];
      const checkedOutProcedures = procedures.filter(({ appointment: { id } }) => id === appointmentId);

      // update procedure price for this appointment
      if (isFullArray(checkedOutProcedures)) {
        for (const checkedOutProcedure of checkedOutProcedures) {
          const checkedOutProcedureId = checkedOutProcedure.id;
          const checkedOutServiceId = checkedOutProcedure.service.id;

          filteredProcedures = procedures.filter(({ id }) => id === checkedOutProcedureId);
          if (isFullArray(filteredProcedures)) {
            const firstFilteredProcedure = first(filteredProcedures);
            const updatedProcedures = servicesOfFirstClient.filter(({ id }) => id === checkedOutServiceId);

            if (isFullArray(updatedProcedures)) {
              firstFilteredProcedure.price = first(updatedProcedures)?.price;

              if (!firstFilteredProcedure.parent_id) delete firstFilteredProcedure.parent_id;

              updatedProcedures.push(firstFilteredProcedure);
            } // if isFullArray updatedProcedure
          } // if isFullArray filteredProcedures
        } // for checkedOutProcedure

        const updatedProceduresIds = updatedProcedures.map(({ id }) => id);

        for (const checkedOutProcedure of checkedOutProcedures) {
          const checkedOutProcedureId = checkedOutProcedure.id;

          filteredProcedures = procedures.filter(({ id }) => id === checkedOutProcedureId);

          if (isFullArray(filteredProcedures)) {
            const firstFilteredProcedure = first(filteredProcedures);
            const firstFilteredProcedureId = firstFilteredProcedure.id;

            if (updatedProceduresIds.indexOf(firstFilteredProcedureId) < 0) {
              if (!firstFilteredProcedure.parent_id) delete firstFilteredProcedure.parent_id;

              updatedProcedures.push(firstFilteredProcedure);
            } // if updatedProceduresIds does not contain firstFilteredProcedureId
          } // if isFullArray filteredProcedures
        } // for checkedOutProcedure
      } // if isFullArray checkedOutProcedures

      if (filteredProcedures && filteredProcedures.length > 0) {
        for (const proc of updatedProcedures) {
          proc.appointment.status = 'paid'; // FIXME: shared mutable state
        }

        const firstProcedure = first(filteredProcedures);
        const clientId = firstProcedure.appointment.client ? firstProcedure.appointment.client.id : null;
        const { notes } = firstProcedure.appointment;
        const appointmentId = firstProcedure.appointment.id;
        const data = { client_id: clientId, procedures: updatedProcedures, notes };

        const capTags2 = { ...capTags1, clientId, appointmentId, action: 'updateAppointmentWithProcedures' };
        const capExtra2 = { ...capExtra1, ...capTags2 };

        L.unignore(
          FTAG,
          'updateAppointmentWithProcedures:',
          updateAppointmentWithProcedures(salonId, appointmentId, data),
        )
          .then((r) => this.captureInfo(capLogger, capTags2, { ...capExtra2, result: r }, CAP_PPU_CREATE_2))
          .catch((e) => this.captureError(capLogger, capTags2, capExtra2, e));
      } // if filteredProcedures is full

      if (appointmentIds.length > 0) {
        // FIXME: shared mutable state, might be a problem, try copying it like {...item} instead
        depositListData = depositListDataFromProps.map((item) =>
          Object.assign(item, { depositType: 'used', used_for_appointmentIds: appointmentIds || [] }),
        );
      } // if appointmentIds is full
    } // for appointmentId

    const txOptions = { ...dataFromProps, depositListData, salonId, appointmentDetails, paymentIntentId, paymentType };
    L.debug(FTAG, 'txOptions:', txOptions);

    const validationResult = validateCheckoutData(txOptions);
    const capExtra3 = { ...capExtra1, ...validationResult };

    if (!validationResult.isValid) {
      this.captureWarn(capLogger, capTags1, capExtra3, CAP_PPU_CREATE_3);
      this.setState({ totalValidate: false });
    }

    try {
      this.setState({ errorMessage: S.empty });
      const result = await beCreateTransaction(txOptions);

      L.debug(FTAG, 'result:', result);
      this.captureInfo(capLogger, capTags1, { ...capExtra3, result }, CAP_PPU_CREATE_4);

      this.setState({ errorMessage: result.is.ok ? S.empty : EMSG.txCreate });
      return result.is.ok;
    } catch (error) {
      L.warn(FTAG, error);

      this.setState({ errorMessage: EMSG.txCreate });
      this.captureError(capLogger, capTags1, capExtra3, error);

      throw error;
    }
  };

  completeTransaction = () => {
    const FTAG = 'completeTransaction()';
    const capLogger = [CTAG, FTAG];

    const { finishUrl, changeAppointmentsStatus, appointments, salonId } = this.props;
    const isWeek = fromPropsToIsWeek(this.props);
    const { stylistId } = fromPropsToMatchedParams(this.props);
    const appointmentIds = asArray(appointments);

    if (appointmentIds.length > 0) {
      const data = appointmentIds.map((id) => ({
        id,
        status: 'paid',
        action: '',
        amount: 0,
        deposit_source: '',
        is_repeating: false,
      }));

      const capTags = { salonId, stylistId, action: 'changeAppointmentsStatus' };
      const capExtra = { ...capTags, isWeek, appointmentIds: appointments, data };

      L.unignore(FTAG, 'changeAppointmentsStatus:', changeAppointmentsStatus(salonId, data))
        .then((r) => this.captureInfo(capLogger, capTags, { ...capExtra, result: r }, CAP_PPU_COMPLETE_1))
        .catch((e) => this.captureError(capLogger, capTags, capExtra, e));
    }

    if (finishUrl && !isWeek) {
      return void navFor(CTAG, FTAG, { isWeek, finishUrl, salonId }, this.props).push(finishUrl);
    }

    return void navFor(CTAG, FTAG, { isWeek, stylistId, salonId }, this.props).push(
      isWeek && stylistId
        ? linkTo({ pattern: LINK.checkoutCompleteWeek, params: { salonId, stylistId } })
        : linkTo({ pattern: LINK.checkoutCompleteDay, params: { salonId } }),
    );
  };

  handleDoubleChargeConfirmV2 = () =>
    void L.unignore('handleDoubleChargeConfirmV2()', 'confirm:', this.confirm()).then(() =>
      this.stopConfirming({ tag: 'handleDoubleChargeConfirmV2' }),
    );

  handleConfirmV2 = () => {
    const { data, posFlow } = this.props;
    const { posStep } = this.state;

    const { onlineAmount, cardAmount } = asObject(data);

    LOG_POS.debug('CheckoutPaymentPopUpV1.handleConfirmV2()', { posFlow, posStep, onlineAmount, cardAmount });
    L.debug('handleConfirmV2()', { posFlow, posStep, onlineAmount, cardAmount });

    if (isPositiveNumber(onlineAmount)) {
      L.debug('handleConfirmV2()', 'Not a positive number onlineAmount:', onlineAmount);
      return void this.setState({ confirmUpdateDialog: true });
    }

    // POS should work only if there is cardAmount, otherwise don't perform that song and dance
    if (isPositiveNumber(cardAmount)) {
      const isFinished = checkIfPosIsFinished({ posFlow, posStep });

      if (isFinished) {
        return void L.unignore('handleConfirmV2()', 'at isFinished:', isFinished, 'confirm:', this.confirm()).finally(
          () => this.stopConfirming({ tag: 'handleConfirmV2--at-isFinished' }),
        );
      }

      if (isNotEnumKey(PRDS, posStep) || posStep === PRDS.closed) {
        LOG_POS.debug('CheckoutPaymentPopUpV1.handleConfirmV2()', 'posStep:', posStep, '->', PRDS.open);
        L.debug('handleConfirmV2()', 'posStep:', posStep, '->', PRDS.open);
        return void this.setState({ posStep: PRDS.open });
      }
    }

    return void L.unignore('handleConfirmV2()', 'at end', 'confirm:', this.confirm()).finally(() =>
      this.stopConfirming({ tag: 'handleConfirmV2--at-end' }),
    );
  };

  handlePosReaderInitV2 = () => void this.startConfirming({ tag: 'handlePosReaderInitV2' });

  handlePosReaderDoneV2 = (ev) => {
    const FTAG = 'handlePosReaderDoneV2()';

    const { canceled, success, paymentIntentId: idFromOptions } = asObject(ev);
    const { paymentIntentId: idFromState } = this.state;

    if (canceled && success) {
      this.captureWarn(
        [CTAG, FTAG],
        { success, canceled, idFromState, idFromOptions },
        { ev, state: this.state },
        CAP_PPU_CONFLICT_2,
      );
    }

    LOG_POS.debug('CheckoutPaymentPopUpV1.handlePosReaderDoneV2()', {
      idFromState,
      idFromOptions,
      success,
      canceled,
      ev,
    });
    L.debug(FTAG, { idFromState, idFromOptions, success, canceled, ev });

    if (canceled) this.handleCancel();

    if (success) {
      L.unignore('handlePosReaderDoneV2()', 'confirm:', this.confirm()).finally(
        () => void this.stopConfirming({ tag: 'handlePosReaderDoneV2--async' }),
      );
    } else {
      this.stopConfirming({ tag: 'handlePosReaderDoneV2--sync' });
    }
  };

  handlePosReaderChange = (options) => {
    LOG_POS.debug('CheckoutPaymentPopUpV1.handlePosReaderChange()', options?.status);
    return void this.setState({ posStep: options?.status });
  };

  handlePosReaderAction = L.wrap('handlePosReaderAction()', (options) => {
    const { pos, clientId, data, appointment, serviceMap, beInitReaderPayment, feSetPosIntentId, salonFlags } =
      this.props;

    const promise = determineAndStorePaymentIntentId({
      appointmentId: asObject(appointment).id,
      amount: asObject(data).cardAmount,
      beInitReaderPayment,
      clientId,
      feSetPosIntentId,
      flags: salonFlags,
      items: determinePriceLines({ data, appointment, serviceMap }), //
      // isOldUsable: false,
      paymentOrigin: PAYMENT_ORIGIN_POS.checkout,
      pos,
      readerId: asObject(options).readerId,
    });

    promise.then((result) => {
      const { paymentIntentId, errorMessage } = asObject(result);
      L.debug('handlePosReaderAction()', 'then()', { paymentIntentId, errorMessage });
      this.setState({ paymentIntentId });
    });

    return promise;
  });

  closePopup = () => {
    const { isFetching } = asObject(this.props.transaction);
    if (isFetching) {
      return;
    }

    navFor(CTAG, 'closePopup()', { isFetching }, this.props).back();
  };

  handleCancel = () => {
    const { cancelChargeSlickCardOnline, data } = this.props;
    const { onlineAmount, cardAmount } = asObject(data);

    this.closePopup();

    if (onlineAmount > 0 || cardAmount > 0) {
      cancelChargeSlickCardOnline();
    }
  };

  goToCalendar = () => void navFor(CTAG, 'goToCalendar()', this.props).goat(-2);

  errorCallback = () => {
    const { cleanTransactionData } = this.props;
    this.setState({ errorMessage: S.empty });
    cleanTransactionData();
    this.closePopup();
  };

  cancelPayment = () => {
    const capLogger = [CTAG, 'cancelPayment()'];
    const { salonId } = this.props;
    const stateUpdate = { function: 'cancelPayment', confirmUpdateDialog: false };

    this.setState(stateUpdate);
    this.captureInfo(capLogger, { salonId }, stateUpdate, CAP_PPU_CANCEL_1);
  };

  handleDoubleChargeConfirm = this.handleDoubleChargeConfirmV2;
  handleConfirm = this.handleConfirmV2;
  handlePosReaderInit = this.handlePosReaderInitV2;
  handlePosReaderDone = this.handlePosReaderDoneV2;

  render() {
    const {
      confirmUpdateDialog,
      totalValidate,
      disableChargeClient,
      posStep,
      errorMessage: errorMessageFromState,
      isConfirming,
    } = this.state;

    const { posFlow, cardOnFileChargeSuccess, transaction, chargingCardError, data, appointment, serviceMap } =
      this.props;

    const { isFetching, errorMessage: errorMessageFromTransaction } = asObject(transaction);

    const { otherAmount, cardAmount, cardNumberEnd, depositAmount, cashAmount, onlineAmount, voucherAmount, total } =
      extractPaymentInfo(this.props);

    const isMismatchDialogOpen = !totalValidate;
    const hasDepositAmount = depositAmount > 0;

    const isPosDialogOpen = checkIfPosIsOpen({ posFlow, cardAmount, posStep });
    const isInPersonDialogOpen = USE_OLD_CARD_PAYMENT_ERROR && chargingCardError && cardOnFileChargeSuccess === false;
    // NOTE: allows for more customized error message to take precedence
    // SEE: https://getslick.atlassian.net/browse/GS-10878
    const errorMessage = isInPersonDialogOpen ? S.empty : errorMessageFromState || errorMessageFromTransaction;

    LOG_POS.debug('CheckoutPaymentPopUpV1()', { isConfirming });

    return (
      <div data-bem="CheckoutPaymentPopUpV1">
        {errorMessage && (
          <AlertDialog
            title={errorMessage}
            buttonText="Close"
            onClick={this.errorCallback}
            onClose={this.errorCallback}
          />
        )}

        <PosReaderDialog
          open={isPosDialogOpen}
          onAction={this.handlePosReaderAction}
          onChange={this.handlePosReaderChange}
          onDone={this.handlePosReaderDone}
          onInit={this.handlePosReaderInit}
        >
          <DialogMessage>
            <PosStateMessage posStep={posStep} price={cardAmount} />
          </DialogMessage>

          {determinePriceLines({ data, appointment, serviceMap }).map(
            ({ finalPrice, clientId, productId, serviceId, ...extra }) => (
              <PosReaderPriceLine {...extra} total={finalPrice} key={`${clientId}-${serviceId}-${productId}`} />
            ),
          )}

          <PosReaderPriceLine description="Total" total={cardAmount} />
        </PosReaderDialog>

        {isInPersonDialogOpen && (
          <ConfirmationDialogFromCheckout
            onRequestClose={this.handleCancel}
            title=" "
            leftButtonText=""
            rightButtonText="OK"
            onRight={this.handleCancel}
            displayHeader="header-non"
          >
            <div className="dialog-inner-title">Charge failed!</div>
            <img src={ICO_FAILED} alt="Client paid" className="dialog-image" />
            <div className="no-show-message center">{EMSG.chargeInPerson}</div>
          </ConfirmationDialogFromCheckout>
        )}

        {fromAnyToBool(confirmUpdateDialog) && (
          <SalonModal tag="CheckoutPaymentPopUpV1__bill" isOpen>
            <SalonModal.Body>
              <div className={CN.modalImage}>
                <div className={CN.iconContainer}>
                  <Icon variant="pay" selected width="lg" />
                </div>
              </div>
              <div className={CN.modalContent}>
                <div className={CN.modalTitle}>
                  <Space>You are about to charge</Space>
                  <span className={CN.textFocused}>
                    <Space>
                      <CurrencySign value={onlineAmount} />
                    </Space>
                  </span>
                  <Space>remotely via SlickPay to</Space>

                  <span className={CN.textFocused}>
                    <Space>client card ending</Space>
                    <Space>{fromAnyToTrimmed(cardNumberEnd)}</Space>
                  </span>
                </div>

                <div className={CN.modalText}>
                  You should <span className={CN.warningText}>NOT take payment via your card machine</span> as this will
                  double charge your client
                </div>
              </div>
            </SalonModal.Body>
            <SalonModal.Footer>
              <div className={CN.modalFooter}>
                <Button variant="outlineForce" onClick={this.cancelPayment}>
                  <span className={CN.buttonSpace}>CANCEL</span>
                </Button>

                <Button variant="primaryForce" onClick={this.handleDoubleChargeConfirm} disabled={disableChargeClient}>
                  <span className={CN.buttonSpace}>CONFIRM</span>
                </Button>
              </div>
            </SalonModal.Footer>
          </SalonModal>
        )}

        {isMismatchDialogOpen && (
          <ConfirmationDialogFromCheckout
            onRequestClose={this.goToCalendar}
            title=" "
            leftButtonText=""
            rightButtonText="OK"
            onRight={this.goToCalendar}
            displayHeader="header-non"
          >
            <div className="dialog-inner-title">Numbers do not add up!</div>
            <img src={ICO_FAILED} alt="Client paid" className="dialog-image" />
            <div className="no-show-message center">
              The balance to pay does not match the total amount to pay, please check and correct.
            </div>
          </ConfirmationDialogFromCheckout>
        )}

        <SalonModal tag="CheckoutPaymentPopUpV1__bill" isOpen>
          <SalonModal.Header>
            <div className={CN.closeButtonContainer}>
              <Button
                tag="CheckoutPaymentPopUpV1__close"
                variant="empty"
                onClick={this.handleCancel}
                disabled={isConfirming}
              >
                <Icon variant="close" variantInvert />
              </Button>
            </div>
          </SalonModal.Header>
          <SalonModal.Body>
            <div className={CN.modalImage}>
              <div className={CN.iconContainer}>
                <Icon variant="pay" selected width="lg" />
              </div>
            </div>
            <div className={CN.modalContent}>
              <div className={CN.modalTitle}>
                Confirm payment for
                <div className={CN.textFocused}>
                  <Space>
                    <CurrencySign value={total} />
                  </Space>
                </div>
              </div>

              <div data-bem="CheckoutPaymentPopUpV1__body" className={CN.modalText}>
                <PayPopUpPriceLine
                  description={`Client card ending ${cardNumberEnd}`}
                  value={onlineAmount}
                  icon={IMG_PAY}
                />
                <PayPopUpPriceLine description="Card in-store" value={cardAmount} icon={IMG_STORE} />
                <PayPopUpPriceLine description="Cash" value={cashAmount} icon={IMG_CASH} />
                <PayPopUpPriceLine description="Voucher" value={voucherAmount} icon={IMG_VOUCHER} />
                <PayPopUpPriceLine description="Other" value={otherAmount} icon={IMG_MONEY} />
                {hasDepositAmount && (
                  <PayPopUpPriceLine description="Deposit redeemed" value={depositAmount} icon={IMG_DEPOSIT} />
                )}
              </div>
            </div>
          </SalonModal.Body>
          <SalonModal.Footer>
            <div data-bem="CheckoutPaymentPopUpV1__buttons">
              {posStep === PRDS.success || posStep === PRDS.closed || isFetching ? (
                <Button tag="CheckoutPaymentPopUpV1__button-loading" variant="primary">
                  <span className={CN.buttonSpace}> Loading...</span>
                </Button>
              ) : (
                <Button tag="CheckoutPaymentPopUpV1__button-confirm" variant="primary" onClick={this.handleConfirm}>
                  <span className={CN.buttonSpace}>CONFIRM</span>
                </Button>
              )}
            </div>
          </SalonModal.Footer>
        </SalonModal>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  salonId: selectCurrentSalonId(state),
  posFlow: selectCurrentSalonPosFlow(state),
  salonFlags: selectCurrentSalonFlags(state),
  clientId: selectCheckoutClientId(state),
  data: selectCheckoutData(state),
  appointment: selectCheckoutSelectedAppointment(state),
  pos: selectCheckoutPos(state),
  serviceMap: selectServiceMap(state),
  checkoutOption: selectCheckoutPaymentOption(state),
  finishUrl: state.checkout.finishUrl,
  appointments: state.checkout.appointments,
  chargingCardError: state.checkout.chargingCardError,
  cardOnFileChargeSuccess: state.checkout.cardOnFileChargeSuccess,
  voucherList: state.cashup.voucherList,
  procedures: state.scheduler.procedures,
  appointmentDetails: state.scheduler.appointmentDetails,
  transaction: state.transaction.create,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      beCreateTransaction,
      cleanTransactionData,
      changeAppointmentsStatus,
      feChangePaymentType,
      useDeposit,
      updateAppointmentWithProcedures,
      updateProcedure,
      beChargeCardOnFile: beChargeCardOnFileV1,
      getAllVoucher,
      getCashupVoucher,
      cancelChargeSlickCardOnline,
      beInitReaderPayment,
      feSetPosIntentId,
    },
    dispatch,
  );

const CheckoutPaymentPopUpV1Hoc = connect(mapStateToProps, mapDispatchToProps)(CheckoutPaymentPopUpV1);

export default CheckoutPaymentPopUpV1Hoc;
